import { ConfigProvider, notification } from 'antd'
import en_US from 'antd/locale/en_US'
import ka_GE from 'antd/locale/ka_GE'
import ru_RU from 'antd/locale/ru_RU'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { RouteList } from 'RouteList'
import { useContext } from 'use-context-selector'
import { withProfiler } from '@sentry/react'

import api from 'services/api'
import { getLocalStorageData, STORAGE_KEY } from 'services/localStorage.service'
import { useStore } from 'store/useStore'
import { PromoPageStateContext } from 'states/PromoPageState/PromoPageStateProvider'
import { setIsChangesUpdatingBlocked, setSections } from 'states/PromoPageState/reducer'
import { setUserRole } from 'states/UserState/reducer'
import { UserStateContext } from 'states/UserState/UserStateProvider'
import { SearchEngine } from 'sections/PromoSection/modules/SearchEngineModule/SearchEngine'
import { Admin } from 'components/AdminV2/Admin'
import { CookiePolicy } from 'components/CookiePolicy/CookiePolicy'
import { layoutModel } from 'components/CookiePolicy/layoutModel'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { ScrollToTop } from 'utils/helpers/ScrollToTop'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { PromoRoutesEnum } from 'shared/constants/routes'
import { colors } from 'shared/constants/values'

import 'assets/css/index.scss'

const App = observer(() => {
  const { viewOrderUiStore, appStore } = useStore()

  const ctx = useContext(UserStateContext)
  const promoCtx = useContext(PromoPageStateContext)

  const { i18n } = useTranslation()

  const [locale, setLocale] = useState<any>(ru_RU)

  const location = useLocation()
  const navigate = useNavigate()

  const isKnowledgeBasePage = location.pathname === PromoRoutesEnum.knowledgeBase

  useEffect(() => {
    //Чтобы была возможность запустить стейдж для грузии
    const params = new URLSearchParams(location.search)
    if (!params.get('isStage')) {
      const isStage = localStorage.getItem('isStage')
      if (isStage) {
        params.set('isStage', 'true')
        navigate(`${location.pathname}?${params.toString()}`, { replace: true })
      }
    }
  }, [location, navigate])

  useEffect(() => {
    //Чтобы была возможность получить доступ к админке и загрузке на сервер без захода через админку
    // Токен из админки необходимо добавить в ручную
    // const token = getLocalStorageData(STORAGE_KEY.USER_TOKEN)
    // token && ctx.dispatch(setUserRole(token))

    void fetchData()

    // тип BeforeInstallPromptEvent не встроен в TypeScript по умолчанию, поэтому TypeScript может не распознавать его напрямую
    const handler = (event: any) => {
      viewOrderUiStore.setPwaEvent(event)
    }

    window.addEventListener('beforeinstallprompt', handler)

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  useEffect(() => {
    if (appStore.user?.info.user_id) {
      window.wba('user_id', { clientId: appStore.user?.info.user_id })
    }
  }, [])

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          newWorker?.addEventListener('statechange', () => {
            if (newWorker.state === 'activated') {
              if (window.confirm('Доступна новая версия! Обновить сейчас?')) {
                window.location.reload()
              }
            }
          })
        })
      })
    }
  }, [])

  useEffect(() => {
    if (window.wba && appStore.user?.info.user_id) {
      window.wba('user_id', { clientId: appStore.user?.info.user_id })
    }
  }, [appStore.user?.info.user_id])

  useEffect(() => {
    if (window.wba) {
      window.wba('pageview')
    }
  }, [location.pathname])

  useAsyncEffect(async () => {
    switch (i18n.language) {
      case 'ru':
        setLocale(ru_RU)
        break
      case 'ka':
        setLocale(ka_GE)
        break
      case 'en':
        setLocale(en_US)
        break

      default:
        setLocale(ru_RU)
        break
    }
  }, [i18n.language])

  const fetchData = async () => {
    try {
      const result = await api.requests.getSections()
      const section = result.sections[2]
      const parsedData = JSON.parse(section.data)

      promoCtx.dispatch(setSections(parsedData.sections))
    } catch (error) {
      promoCtx.dispatch(setIsChangesUpdatingBlocked())
      notification.error({ message: `Не удалось загрузить данные с сервера. ${(error as Error).message}` })
    }
  }

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        token: {
          colorPrimary: colors.corp,
          fontSize: 16,
        },
        components: {
          Typography: {
            titleMarginBottom: 0,
          },
          Form: {
            itemMarginBottom: 16,
          },
        },
      }}
    >
      <ScrollToTop />

      <div className="app">
        <CookiePolicy layoutModel={layoutModel} />
        <RouteList />
        {!isGeDomain() && !window.location.pathname.match('/login') && !isKnowledgeBasePage && <SearchEngine />}
      </div>

      {ctx.state.adminRole && <Admin />}
    </ConfigProvider>
  )
})

export default withProfiler(App)
